import * as React from 'react';
import { BookmarkAddOutlined } from '@mui/icons-material';

import {
  Box,
  Autocomplete,
  TextField,
  Popover,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  UseMutation,
  UseLazyQuery,
  UseQueryState,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';

export interface TagInputProps {
  options?: any[];
  data?: any[];
  lazyQuery?: UseLazyQuery<any>;
  queryParams?: any;
  subscription?: UseQueryState<any>;
  createMutation?: UseMutation<any>;
  deleteMutation?: UseMutation<any>;
  disabled?: boolean;
  onCreate?: Function;
  hover?: boolean;
  reloadParent?: Function;
  force?: boolean;
}

export const TagsAutocompleteInput = (params: TagInputProps) => {
  const {
    options,
    data: originalData,
    lazyQuery,
    queryParams,
    subscription,
    createMutation,
    deleteMutation,
    disabled = false,
    onCreate,
    hover = false,
    force = false,
  } = params;

  const { data: tagsCache } = (subscription && subscription(queryParams)) || {};
  const [reload] = (lazyQuery && lazyQuery()) || [() => {}];
  const [create] = (createMutation && createMutation()) || [() => {}];
  const [remove] = (deleteMutation && deleteMutation()) || [() => {}];
  const [anchorEl, setAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);

  const [data, setData] = React.useState<any[]>(
    originalData?.map((r: any) => ({ ...r, ...r.tag })) || []
  );

  React.useEffect(() => {
    if (force && originalData) {
      setData(originalData?.map((r: any) => ({ ...r, ...r.tag })));
    }
  }, [force, originalData]);

  React.useEffect(() => {
    if (tagsCache && Array.isArray(tagsCache)) {
      setData(tagsCache?.map((r: any) => ({ ...r, ...r.tag })));
    }
  }, [tagsCache]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const [loading, setLoading] = React.useState<boolean>(false);
  return (
    <>
      {((data && data.length > 0) || hover) && (
        <>
          <Tooltip
            arrow
            placement="left-start"
            title={!disabled ? 'Click to add or modify tags' : ''}
          >
            <Box
              onClick={(event: any) => {
                event.preventDefault();
                event.stopPropagation();
                if (!disabled) {
                  handleClick(event);
                }
              }}
            >
              {data && data.length > 0 ? (
                data.map((d: any) => (
                  <Chip
                    label={d.name}
                    sx={{
                      boxShadow: theme => theme.shadows[1],
                      borderRadius: '16px',
                      border: theme => `1px solid ${theme.palette.grey[200]}`,
                      backgroundColor: theme => theme.palette.grey[50],
                    }}
                  />
                ))
              ) : (
                <BookmarkAddOutlined color="primary" />
              )}
            </Box>
          </Tooltip>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={(event: any) => {
              event.preventDefault();
              event.stopPropagation();
              handleClose();
            }}
            onClick={(event: any) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <Box sx={{ p: 2, width: '250px' }}>
              <Autocomplete
                multiple
                disableClearable
                sx={{ width: '100%' }}
                options={
                  options?.filter(
                    (t: any) => !data?.find((r: any) => t.id === r.tag)
                  ) || []
                }
                getOptionLabel={(option: any) => {
                  if (typeof option === 'string') {
                    return option;
                  } else {
                    return option?.label || option?.title || option?.name || '';
                  }
                }}
                value={data}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    // variant="standard"
                    label={'Tag'}
                    multiline={true}
                    InputProps={{
                      ...(loading
                        ? {
                            ...params.InputProps,
                            endAdornment: (
                              <CircularProgress color="inherit" size={10} />
                            ),
                          }
                        : params.InputProps),
                    }}
                  />
                )}
                onChange={async (event: any, items: any) => {
                  if (items && Array.isArray(items) && items.length > 0) {
                    setLoading(true);
                    const newItem = items?.find(
                      (i: any) => !data.find((j: any) => j.tag === i.tag)
                    );
                    if (newItem && create && onCreate) {
                      await create(onCreate(newItem));
                      await reload(queryParams);
                    }
                    setLoading(false);
                  }
                }}
                renderTags={(value: readonly string[], getTagProps: any) => {
                  return value.map((option: any, index: number) => {
                    return (
                      <Chip
                        variant="outlined"
                        color="secondary"
                        label={option.name}
                        size="small"
                        {...getTagProps({ index })}
                        onDelete={async props => {
                          setLoading(true);
                          if (remove && option.id) {
                            await remove(option.id);
                            await reload(queryParams);
                            getTagProps({ index }).onDelete(props);
                          }
                          setLoading(false);
                        }}
                      />
                    );
                  });
                }}
              />
            </Box>
          </Popover>
        </>
      )}
    </>
  );
};
